// REL_1.1.15F F-321 (S-1245) — App-store download badges, RUNTIME-configurable (NO redeploy).
// URLs + per-platform show/hide come from a public config JSON blob (Infra-hosted on the marketing CDN,
// short-TTL/cache-busted): `{ ios: { url, visible }, android: { url, visible } }`. A platform with
// visible:false (or no url) renders NOTHING — no dead link — until its store listing is live and the
// operator flips the blob flag. Graceful: if the config is unset or unreachable, the component renders
// nothing (never a broken badge). Official store artwork lives in `public/app-badges/` (Marketing/Infra
// supply per Apple/Google brand guidelines); until then the badges stay hidden via the blob flags.
import { useEffect, useState } from "react";

interface PlatformConfig {
  url: string;
  visible: boolean;
}
interface AppLinksConfig {
  ios?: PlatformConfig;
  android?: PlatformConfig;
}

const CONFIG_URL = import.meta.env.VITE_APP_LINKS_CONFIG_URL as string | undefined;

export default function AppDownloadBadges({ className }: { className?: string }) {
  const [cfg, setCfg] = useState<AppLinksConfig | null>(null);

  useEffect(() => {
    if (!CONFIG_URL) return; // no config source wired → render nothing
    const ctrl = new AbortController();
    // Cache-bust with a coarse minute-bucket so an operator's blob edit goes live within ~a minute,
    // without a redeploy, while still allowing CDN caching within the bucket.
    const bust = Math.floor(Date.now() / 60000);
    const sep = CONFIG_URL.includes("?") ? "&" : "?";
    fetch(`${CONFIG_URL}${sep}t=${bust}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? (r.json() as Promise<AppLinksConfig>) : null))
      .then((data) => { if (data) setCfg(data); })
      .catch(() => { /* unreachable / aborted → render nothing */ });
    return () => ctrl.abort();
  }, []);

  const ios = cfg?.ios;
  const android = cfg?.android;
  const showIos = !!ios?.visible && !!ios.url;
  const showAndroid = !!android?.visible && !!android.url;
  if (!showIos && !showAndroid) return null;

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className ?? ""}`} data-testid="app-download-badges">
      {showIos && (
        <a href={ios!.url} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store">
          <img src="/app-badges/app-store.svg" alt="Download on the App Store" className="h-10 w-auto" />
        </a>
      )}
      {showAndroid && (
        <a href={android!.url} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play">
          <img src="/app-badges/google-play.svg" alt="Get it on Google Play" className="h-10 w-auto" />
        </a>
      )}
    </div>
  );
}
